import React, { useCallback, useEffect } from "react";
import { WizardService } from "../../../services/WizardService";
import { Sdk, SdkCategory, SdkStability } from "../../../types/sdk";
import {
  Alert,
  Breadcrumb,
  Button,
  Drawer,
  Layout,
  message,
  Tag,
  Descriptions,
  Row,
  Skeleton,
  Col,
  Space,
  Spin,
  Typography,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { SdkForm } from "./SdkForm";
import { AppRoutes } from "../../../AppRoutes";
import { IntegrationTable } from "../integration/IntegrationTable";
import { SpmIntegrationTable } from "../integration/SpmIntegrationTable";
import {
  nativeIntegrationLabel,
  supportsSpm,
} from "../integration/platformIntegration";
import { Integration } from "../../../types/integration";
import { SpmIntegrationInfo } from "../../../types/spmIntegrationInfo";
import { IntegrationForm } from "../integration/IntegrationForm";
import { Adapter } from "../../../types/adapter";
import { DropdownAction } from "../../components/ColumnBuilder";
import { GroupedAdapterList } from "../adapter/GroupedAdapterList";
import { ResponsiveCard } from "../../components/ResponsiveCard";
import { SuggestedAdapterList } from "../adapter/SuggestedAdapterList";
import { IntegrationPreview } from "../../components/IntegrationPreview";
import { PlatformIcon } from "../../components/PlatformIcon";
import { Badge } from "../../../types/badge";
import { BadgeTable } from "../badge/BadgeTable";
import { BadgeCreateForm } from "../badge/BadgeCreateForm";
import { MediationType, ValidationResponse } from "../../../types/validator";
import { ValidatorDrawer } from "../../validator/ValidatorDrawer";
import { ValidationStats } from "../../validator/ValidationStats";

const { Content } = Layout;
const { Text } = Typography;

// The SDK's `status` field identifies which mediation provider a mediation
// SDK release belongs to. Most values match the validator's MediationType
// 1:1, except AppLovin MAX, whose SDK status is the legacy "max" engine key
// while the validator API expects "applovinmax".
const SDK_STATUS_TO_MEDIATION_TYPE: Partial<Record<string, MediationType>> = {
  appodeal: "appodeal",
  max: "applovinmax",
  levelplay: "levelplay",
  bidon: "bidon",
  bidmachine: "bidmachine",
};

export const SdkViewPage = () => {
  const api = WizardService.Sdk;
  const navigate = useNavigate();

  const { id } = useParams();

  const [isSdkLoading, setSdkLoading] = React.useState<boolean>(false);
  const [isSdkEditing, setSdkEditing] = React.useState<boolean>(false);
  const [sdk, setSdk] = React.useState<Sdk>();

  const [isIntegrationsLoading, setIntegrationsLoading] =
    React.useState<boolean>(false);
  const [isAddingIntegration, setAddingIntegration] =
    React.useState<boolean>(false);
  const [integrations, setIntegrations] = React.useState<Integration[]>([]);

  // SPM rows anchored on this SDK. A given SPM row anchors on an adapter XOR an SDK, so this
  // list is disjoint from what the adapter pages show — an SDK-anchored row is visible nowhere
  // else in the app.
  const [isSpmLoading, setSpmLoading] = React.useState<boolean>(false);
  const [spmIntegrations, setSpmIntegrations] = React.useState<
    SpmIntegrationInfo[]
  >([]);

  const [isAdaptersLoading, setAdaptersLoading] =
    React.useState<boolean>(false);
  const [isAddingAdapter, setAddingAdapter] = React.useState<boolean>(false);
  const [adapters, setAdapters] = React.useState<Adapter[]>([]);

  const [isBadgesLoading, setBadgesLoading] = React.useState<boolean>(false);
  const [isAddingBadge, setAddingBadge] = React.useState<boolean>(false);
  const [badges, setBadges] = React.useState<Badge[]>([]);

  const [isValidatorOpen, setValidatorOpen] = React.useState<boolean>(false);
  const [isValidating, setValidating] = React.useState<boolean>(false);
  const [validationResult, setValidationResult] =
    React.useState<ValidationResponse | null>(null);
  const [validationError, setValidationError] = React.useState<string | null>(
    null
  );

  // Only mediation SDKs whose status maps to a mediation the Validator
  // understands get the Validate entry; everything else keeps Badges.
  const mediationType: MediationType | undefined =
    sdk?.category === SdkCategory.MEDIATION
      ? SDK_STATUS_TO_MEDIATION_TYPE[sdk.status]
      : undefined;

  const saveSdk = (record: Sdk) => {
    setSdkLoading(true);
    let promise: Promise<any>;
    if (record.id !== undefined && record.id > 0) {
      promise = api.update(record.id, record);
    } else {
      promise = api.create(record);
    }

    promise
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => {
        setSdkEditing(false);
        setSdkLoading(false);
      });
  };

  const deleteIntegration = (integrationId: number) => {
    setIntegrationsLoading(true);
    api
      .removeIntegration(parseInt(id!), integrationId)
      .then(() => fetchIntegrations())
      .catch((error) => {
        message.error(error.message);
        setIntegrationsLoading(false);
      });
  };

  const addIntegration = (record: Integration) => {
    setIntegrationsLoading(true);
    api
      .addIntegration(parseInt(id!), record)
      .then(() => fetchIntegrations())
      .catch((error) => {
        message.error(error.message);
        setIntegrationsLoading(false);
      });
  };

  const deleteAdapter = (adapterId: number) => {
    setAdaptersLoading(true);
    api
      .removeAdapter(parseInt(id!), adapterId)
      .then(() => fetchAdapters())
      .catch((error) => {
        message.error(error.message);
        setAdaptersLoading(false);
      });
  };

  const addAdapters = (records: Adapter[]) => {
    setAdaptersLoading(true);
    Promise.all(
      records.map((adapter) => api.addAdapter(parseInt(id!), adapter))
    )
      .then(() => fetchAdapters())
      .catch((error) => {
        message.error(error.message);
        setAdaptersLoading(false);
      });
  };

  const deleteBadge = (badgeId: number) => {
    setBadgesLoading(true);
    WizardService.Badge.delete(badgeId)
      .then(() => fetchBadges())
      .catch((error) => message.error(error.message))
      .finally(() => setBadgesLoading(false));
  };

  const addBadge = (record: Badge) => {
    setBadgesLoading(true);
    const badgeWithSdkId = { ...record, sdkId: parseInt(id!) };
    WizardService.Badge.create(badgeWithSdkId)
      .then(() => {
        fetchBadges();
        setAddingBadge(false);
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
        message.error(errorMessage);
      })
      .finally(() => setBadgesLoading(false));
  };

  // Callbacks
  const fetch = useCallback(() => {
    setSdkLoading(true);
    api
      .one(parseInt(id!))
      .then((sdk) => setSdk(sdk))
      .catch((error) => message.error(error.message))
      .finally(() => setSdkLoading(false));
  }, [api, id]);

  const fetchIntegrations = useCallback(() => {
    setIntegrationsLoading(true);
    api
      .integration(parseInt(id!))
      .then((integrations) => setIntegrations(integrations || []))
      .catch((error) => message.error(error.message))
      .finally(() => setIntegrationsLoading(false));
  }, [api, id]);

  const fetchSpmIntegrations = useCallback(() => {
    setSpmLoading(true);
    api
      .spmIntegrations(parseInt(id!))
      .then((records) => setSpmIntegrations(records || []))
      .catch((error) => message.error(error.message))
      .finally(() => setSpmLoading(false));
  }, [api, id]);

  const fetchAdapters = useCallback(() => {
    setAdaptersLoading(true);
    api
      .adapters(parseInt(id!))
      .then((adapters) => setAdapters(adapters || []))
      .catch((error) => message.error(error.message))
      .finally(() => setAdaptersLoading(false));
  }, [api, id]);

  const fetchBadges = useCallback(() => {
    setBadgesLoading(true);
    api
      .badges(parseInt(id!))
      .then((badges) => setBadges(badges || []))
      .catch((error) => message.error(error.message))
      .finally(() => setBadgesLoading(false));
  }, [api, id]);

  const validate = useCallback(() => {
    if (!mediationType || !sdk) return;

    setValidating(true);
    setValidationError(null);
    WizardService.Validator.validate(mediationType, sdk.platform, sdk.version)
      .then((result) => setValidationResult(result))
      .catch((err: any) => {
        setValidationError(err.response?.data?.message || "Validation failed");
      })
      .finally(() => setValidating(false));
  }, [mediationType, sdk]);

  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // Platform-gated: an Android SDK can never have SPM rows, so skip the guaranteed-empty request
  // and the card that would render from it. `sdk` arrives async, so this no-ops until it lands.
  useEffect(() => {
    if (supportsSpm(sdk?.platform)) {
      fetchSpmIntegrations();
    }
  }, [fetchSpmIntegrations, sdk?.platform]);

  useEffect(() => {
    fetchAdapters();
  }, [fetchAdapters]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  useEffect(() => {
    validate();
  }, [validate]);

  const description = () => {
    switch (sdk?.category) {
      case SdkCategory.AD_NETWORK:
        return "Ad Network Sdk";
      case SdkCategory.MEDIATION:
        return "Mediation Sdk";
      case SdkCategory.SERVICE:
        return "Service Sdk";
      case SdkCategory.OTHER:
        return "Other Sdk";
    }
  };

  const stability = () => {
    switch (sdk?.stability) {
      case SdkStability.STABLE:
        return (
          <Tag style={{ marginInlineEnd: 0 }} color="green">
            STABLE
          </Tag>
        );
      case SdkStability.BETA:
        return (
          <Tag style={{ marginInlineEnd: 0 }} color="orange">
            BETA
          </Tag>
        );
      case SdkStability.ALPHA:
        return (
          <Tag style={{ marginInlineEnd: 0 }} color="red">
            ALPHA
          </Tag>
        );
      case SdkStability.CUSTOM:
        return (
          <Tag style={{ marginInlineEnd: 0 }} color="gray">
            CUSTOM
          </Tag>
        );
    }
  };

  const formattedDate = (date?: string) => {
    if (date) {
      return new Date(date).toDateString();
    }
  };

  return (
    <Layout>
      <Content>
        <Row style={{ margin: "1rem 0" }}>
          <Breadcrumb
            items={[
              {
                href: "/" + AppRoutes.Wizard.SdkList,
                title: "SDK Releases",
                onClick: () => {
                  navigate(-1);
                },
              },
              {
                title: "Sdk #" + id,
              },
            ]}
          />
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <ResponsiveCard
              title={sdk?.name}
              contentPadding="1rem"
              extra={
                <Button
                  type="text"
                  icon={<EditOutlined  />}
                  onClick={() => setSdkEditing(true)}
                />
              }
            >
              {isSdkLoading ? (
                <Skeleton active />
              ) : (
                <Descriptions
                  column={2}
                  items={[
                    {
                      key: "1",
                      label: "OS",
                      children: sdk && (
                        <PlatformIcon platform={sdk?.platform} />
                      ),
                    },
                    { key: "2", label: "Type", children: description() },
                    { key: "3", label: "Status", children: sdk?.status },
                    { key: "4", label: "Version", children: sdk?.version },
                    { key: "5", label: "Stability", children: stability() },
                    {
                      key: "6",
                      label: "Created",
                      children: formattedDate(sdk?.createdAt),
                    },
                    {
                      key: "7",
                      label: "Updated",
                      children: formattedDate(sdk?.updatedAt),
                    },
                    {
                      key: "8",
                      label: "Reindex",
                      children: sdk?.reindexEnabled ? (
                        <Tag color="green">ENABLED</Tag>
                      ) : (
                        <Tag color="default">DISABLED</Tag>
                      ),
                    },
                  ]}
                />
              )}
            </ResponsiveCard>
          </Col>
          <Col span={12}>
            <ResponsiveCard
              title={`Integration — ${nativeIntegrationLabel(sdk?.platform)}`}
              extra={
                <Button
                  type="text"
                  icon={<PlusOutlined  />}
                  onClick={() => setAddingIntegration(true)}
                />
              }
            >
              <IntegrationTable
                loading={isIntegrationsLoading}
                records={integrations}
                onDelete={deleteIntegration}
              />
            </ResponsiveCard>
            {supportsSpm(sdk?.platform) && (
              <ResponsiveCard
                title={"Integration — SPM"}
                style={{ marginTop: "1rem" }}
              >
                <SpmIntegrationTable
                  loading={isSpmLoading}
                  records={spmIntegrations}
                />
              </ResponsiveCard>
            )}
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            {mediationType ? (
              <ResponsiveCard
                title={"Validate"}
                contentPadding="1rem"
                extra={
                  !isValidating &&
                  !validationError &&
                  validationResult && (
                    <Button onClick={() => setValidatorOpen(true)}>
                      Details
                    </Button>
                  )
                }
              >
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  <Text type="secondary">
                    Mediation validator for {mediationType} {sdk?.platform} v
                    {sdk?.version}.
                  </Text>
                  {isValidating ? (
                    <Space>
                      <Spin size="small" />
                      <Text type="secondary">Validating…</Text>
                    </Space>
                  ) : validationError ? (
                    <Alert
                      type="error"
                      message={validationError}
                      showIcon
                      action={
                        <Button size="small" onClick={validate}>
                          Retry
                        </Button>
                      }
                    />
                  ) : (
                    validationResult && (
                      <ValidationStats summary={validationResult.summary} />
                    )
                  )}
                </Space>
              </ResponsiveCard>
            ) : (
              <ResponsiveCard
                title={"Badges"}
                extra={
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => setAddingBadge(true)}
                  />
                }
              >
                <BadgeTable
                  loading={isBadgesLoading}
                  records={badges}
                  onDelete={deleteBadge}
                />
              </ResponsiveCard>
            )}
          </Col>
        </Row>
        {sdk?.category === SdkCategory.MEDIATION && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <ResponsiveCard
                  title={"Adapters"}
                  extra={
                    <Button
                      type="text"
                      icon={<PlusOutlined  />}
                      onClick={() => setAddingAdapter(true)}
                    />
                  }
                >
                  <GroupedAdapterList
                    loading={isAdaptersLoading}
                    records={adapters}
                    actions={[DropdownAction.VIEW, DropdownAction.DELETE]}
                    removeRecord={deleteAdapter}
                    viewRecord={(id) =>
                      navigate(
                        "/" +
                          AppRoutes.Wizard.Adapter.replace(":id", id.toString())
                      )
                    }
                    editable
                  />
                </ResponsiveCard>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <ResponsiveCard
                  title={"Integration Preview"}
                  contentPadding="1rem"
                >
                  {/* No cache-busting `key` needed here: IntegrationPreview's own fetch chain is
                      keyed off `sdk.version`/`sdk.platform`/`sdk.status` directly, not off the
                      legacy `adapters` CRUD list the old MediationIntegrationPreview depended on. */}
                  <IntegrationPreview sdk={sdk} />
                </ResponsiveCard>
              </Col>
            </Row>
          </>
        )}
        <IntegrationForm
          open={isAddingIntegration}
          onCancel={() => setAddingIntegration(false)}
          onSave={(record) => addIntegration(record)}
        />
        <BadgeCreateForm
          open={isAddingBadge}
          onCancel={() => setAddingBadge(false)}
          onCreate={(record) => addBadge(record)}
        />
        <SuggestedAdapterList
          sdkId={sdk?.id}
          open={isAddingAdapter}
          onAdapterSelected={(records) => addAdapters(records)}
          onClose={() => setAddingAdapter(false)}
        />
        {mediationType && sdk && (
          <ValidatorDrawer
            open={isValidatorOpen}
            onClose={() => setValidatorOpen(false)}
            title={`Validate ${mediationType} ${sdk.platform} v${sdk.version}`}
            result={validationResult}
            loading={isValidating}
            error={validationError}
          />
        )}
        <Drawer
          title={"Edit Sdk Release #" + id}
          width={600}
          onClose={(_) => setSdkEditing(false)}
          open={isSdkEditing}
          styles={{
            body: {
              paddingBottom: 80,
            },
          }}
        >
          <SdkForm sdk={sdk} isLoading={isSdkLoading} onFinish={saveSdk} />
        </Drawer>
      </Content>
    </Layout>
  );
};
